import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useRouter, useSearch } from '@tanstack/react-router';
import { keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { toast } from '@blimo/ui';
import { ForgotPasswordForm, LoginForm, ResetPasswordForm, TwoFactorChallengeForm } from '@blimo/ui/auth';
import { PortalAuthLayout } from '../components/ui/auth-layout.jsx';
import { useAppName } from '../lib/app-name.js';
import { safeRedirect } from '../lib/redirect.js';

/** Anmeldeseiten mit dem Namen aus dem Laufzeit-Branding. @param {Omit<Parameters<typeof PortalAuthLayout>[0], 'appName'>} props */
function AuthLayout(props) {
  return <PortalAuthLayout {...props} appName={useAppName()} />;
}

function BackToLogin() {
  return (
    <Link to="/login" search={{ redirect: undefined }} className="inline-flex min-h-8 items-center font-medium text-primary underline-offset-4 hover:underline">
      Zurück zur Anmeldung
    </Link>
  );
}

/** Drops cached /me and continues to the redirect target. */
function useFinishLogin() {
  const queryClient = useQueryClient();
  const router = useRouter();
  return async (redirect) => {
    queryClient.removeQueries({ queryKey: keys.me() });
    await router.navigate({ href: safeRedirect(redirect), replace: true });
  };
}

export function LoginPage() {
  const api = useApi();
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const finish = useFinishLogin();

  async function login(values) {
    const result = await api.auth.login(values);
    if (result.two_factor) {
      await navigate({ to: '/two-factor-challenge', search: { redirect: search.redirect } });
    } else {
      await finish(search.redirect);
    }
  }

  return (
    <AuthLayout title="Anmelden" description="Kundenportal für Eigentümer, Mandanten, Kassenprüfer und Ableser.">
      <LoginForm
        onSubmit={login}
        forgotPasswordLink={
          <Link to="/forgot-password" className="inline-flex items-center text-[0.8125rem] font-medium text-primary underline-offset-4 hover:underline">
            Passwort vergessen?
          </Link>
        }
      />
    </AuthLayout>
  );
}

export function TwoFactorChallengePage() {
  const api = useApi();
  const search = useSearch({ strict: false });
  const finish = useFinishLogin();
  return (
    <AuthLayout title="Zwei-Faktor-Bestätigung" description="Geben Sie den Code aus Ihrer Authenticator-App ein." footer={<BackToLogin />}>
      <TwoFactorChallengeForm
        onSubmit={async (payload) => {
          await api.auth.twoFactorChallenge(payload);
          await finish(search.redirect);
        }}
      />
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const api = useApi();
  return (
    <AuthLayout title="Passwort vergessen" description="Wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts." footer={<BackToLogin />}>
      <ForgotPasswordForm onSubmit={(values) => api.auth.forgotPassword(values)} />
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const api = useApi();
  const navigate = useNavigate();
  const { token } = useParams({ strict: false });
  const search = useSearch({ strict: false });
  return (
    <AuthLayout title="Neues Passwort festlegen" footer={<BackToLogin />}>
      <ResetPasswordForm
        defaultEmail={typeof search.email === 'string' ? search.email : ''}
        onSubmit={async (values) => {
          await api.auth.resetPassword({ ...values, token });
          toast.success('Ihr Passwort wurde geändert. Bitte melden Sie sich an.');
          await navigate({ to: '/login', search: { redirect: undefined } });
        }}
      />
    </AuthLayout>
  );
}
